import { getCampaignLeadCostQueryOptions } from "@/api/campaign.api";
import { creditsQueryOptions } from "@/api/credits.api";
import {
  ContactInformationForm,
  contactInformationFormSchema,
  ContactInformationFormValues,
} from "@/components/campaign/setup/planning/ContactInformationForm";
import { AlertModal } from "@/components/generic/AlertModal";
import { Button } from "@/components/generic/Button";
import { PageHeaderProvider } from "@/providers/PageHeaderProvider";
import { getCampaignUpdateCreditStats } from "@/services/credits.service";
import { NewCampaign } from "@/types/campaign";
import { useStepperSubmitHandlerState } from "@/utils/stepper";
import {
  createFileRoute,
  HistoryState,
  useBlocker,
} from "@tanstack/react-router";
import { useMemo } from "react";
import z from "zod";
import { Route as BuyCreditsRoute } from "./buy-credits";

const historyStateSchema = z
  .object({
    values: contactInformationFormSchema,
    autoSubmit: z.boolean(),
  })
  .optional()
  .catch(undefined);

function parseHistoryState(historyState: z.infer<typeof historyStateSchema>) {
  return historyStateSchema.parse(historyState);
}

export const Route = createFileRoute(
  "/_dashboard/campaigns/$id/(edit)/planning/contact-information/",
)({
  component: RouteComponent,
  beforeLoad: ({ location }) => {
    const historyState = parseHistoryState(
      location.state.routeStates?.[Route.id],
    );

    return {
      parseHistoryState,
      useHistoryState: () => historyState,
    };
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const context = Route.useRouteContext();
  const { campaign, updateCampaign } = context.useEditCampaign();
  const stepper = context.usePlanningStepper();

  const step = stepper.flatStepsById["planning/contact-information"];

  const stepperContext = useMemo(() => {
    if (!step.getIsVisible(stepper.context))
      throw Error("Step is not visible with current context");
    return stepper.context;
  }, [step, stepper.context]);

  const { delivery: deliveryContext, planning: planningContext } =
    stepperContext;

  const historyState = context.useHistoryState();

  const defaultValues = useMemo(
    (): ContactInformationFormValues => ({
      deliverContacts: planningContext.deliverContacts ?? false,
      deliverContactsRequirePhone:
        planningContext.deliverContactsRequirePhone ?? false,
      deliverLiveNumber: planningContext.deliverLiveNumber ?? false,
    }),
    [
      planningContext.deliverContacts,
      planningContext.deliverContactsRequirePhone,
      planningContext.deliverLiveNumber,
    ],
  );

  const values = useMemo(
    () => historyState?.values ?? defaultValues,
    [defaultValues, historyState?.values],
  );

  const [submitHandler, setSubmitHandler] = useStepperSubmitHandlerState();

  const blocker = useBlocker({
    shouldBlockFn: ({ next }) => {
      if (
        next.fullPath === Route.fullPath ||
        next.fullPath === BuyCreditsRoute.fullPath
      )
        return false;
      return submitHandler.isDirty ?? false;
    },
    withResolver: true,
  });

  return (
    <PageHeaderProvider
      options={{
        actions: (
          <>
            <Button
              isDisabled={submitHandler.isDisabled || !submitHandler.isDirty}
              isPending={submitHandler.isPending}
              onPress={() => submitHandler.submit()}
            >
              Gem
            </Button>
          </>
        ),
      }}
    >
      <ContactInformationForm
        configurations={stepperContext.configurations}
        exportSystem={deliveryContext.exportSystem ?? null}
        defaultValues={defaultValues}
        values={values}
        updateValues={async (values) => {
          if (!historyState?.autoSubmit && campaign.status === "Active") {
            const campaignWithNewData: NewCampaign = {
              ...campaign,
              ...values,
            };

            const [credits, campaignLeadCost, updatedCampaignLeadCost] =
              await Promise.all([
                context.queryClient.ensureQueryData(creditsQueryOptions),
                context.queryClient.ensureQueryData(
                  getCampaignLeadCostQueryOptions(campaign),
                ),
                context.queryClient.ensureQueryData(
                  getCampaignLeadCostQueryOptions(campaignWithNewData),
                ),
              ]);

            const {
              updatedCreditsNeededRestOfPeriod,
              updatedCreditsNeededNextPeriod,
            } = getCampaignUpdateCreditStats({
              credits,
              campaignLeadCost,
              updatedCampaignLeadCost,
            });

            const neededCredits = Math.max(
              updatedCreditsNeededRestOfPeriod,
              updatedCreditsNeededNextPeriod,
            );

            if (neededCredits) {
              const state = {
                routeStates: {
                  [Route.id]: {
                    values,
                    autoSubmit: false,
                  },
                  [BuyCreditsRoute.id]: {
                    neededCredits,
                  },
                },
              } satisfies HistoryState;

              await navigate({
                replace: true,
                state,
              });

              await navigate({
                to: BuyCreditsRoute.to,
                state,
              });
              return;
            }
          }

          await updateCampaign(values);
        }}
        setSubmitHandler={setSubmitHandler}
        linkToContactsFilter={{
          to: "/campaigns/$id/define/$category/contacts",
          params: { category: "contacts" },
        }}
        autoSubmit={historyState?.autoSubmit}
      />

      <AlertModal
        isOpen={blocker.status === "blocked"}
        title="Du har ikke-gemte ændringer"
        description="Dine ændringer vil gå tabt. Er du sikker på at du vil fortsætte?"
        onClose={blocker.reset}
        onConfirm={() => {
          blocker.proceed?.();
        }}
      />
    </PageHeaderProvider>
  );
}
